import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  PhotoLibrary as PhotoIcon,
  Router as RouterIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import DashboardSidebar, { DashboardSection, MobileSectionNav } from './DashboardSidebar';
import OverviewSection from './sections/OverviewSection';
import UnifiedScheduleSection from './sections/UnifiedScheduleSection';
import PlaceholderSection from './sections/PlaceholderSection';
import NetworkSpreadsheet from '../SiteSettingsPage/NetworkSpreadsheet';
import NvrTemplateSpreadsheet from '../SiteSettingsPage/NvrTemplateSpreadsheet';
import AccountSpreadsheet from '../SiteSettingsPage/AccountSpreadsheet';
import PhotoGalleryView from './sections/PhotoGalleryView';
import DeploySection from './sections/DeploySection';
import WallpadMapSection from './sections/WallpadMapSection';
import ParkingAgentSection from 'pages/ProjectDashboardPage/sections/ParkingAgentSection';

interface StoredProject {
  id?: string | number;
  name?: string;
  location?: string;
  status?: string;
  dashboard_url?: string;
  construction_completed_at?: string | null;
  final_completed_at?: string | null;
}

// 자재/히스토리/문서 보관함은 이 페이지 내부 section state 가 아니라 별도 라우트 —
// ?section= 쿼리로 들어오거나(다른 페이지에서 사이드바로 복귀) 사이드바 클릭 시
// 항상 이 라우트로 navigate 한다. renderSection() 은 이 값들을 다루지 않는다.
const EXTERNAL_SECTIONS: DashboardSection[] = ['history', 'materials', 'documents', 'qa'];

function externalRouteFor(s: DashboardSection, projectIdNum: number): string | null {
  // 예전 'QA 주차 테스트' 메뉴 링크(?section=parkingQa)는 QA 페이지의 주차 테스트 탭으로.
  if ((s as string) === 'parkingQa') return `/project/${projectIdNum}/qa?tab=parking`;
  switch (s) {
    case 'history':
      return `/audit-logs?project=${projectIdNum}`;
    case 'materials':
      return `/project/${projectIdNum}/materials`;
    case 'documents':
      return `/project/${projectIdNum}/documents`;
    case 'qa':
      return `/project/${projectIdNum}/qa`;
    default:
      return null;
  }
}

const ProjectDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  const { mode, toggleTheme } = useContext(ThemeContext);

  const selectedProject = localStorage.getItem('selectedProject');
  const projectData: StoredProject | null = selectedProject ? JSON.parse(selectedProject) : null;
  const projectIdNum = projectId ? Number(projectId) : NaN;

  const formatDate = (iso?: string | null): string | null => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('ko-KR');
    } catch {
      return iso;
    }
  };

  const constructionDate = formatDate(projectData?.construction_completed_at);
  const finalDate = formatDate(projectData?.final_completed_at);
  const siteName = projectData?.name || `프로젝트 ${projectId}`;

  // 사이드바에서 선택된 섹션. 페이지 라우트는 바뀌지 않고 페이지 내부 state 로만 관리.
  // 다른 페이지(자재/히스토리/문서 보관함)에서 사이드바를 통해 돌아올 때 ?section=xxx
  // 쿼리로 초기 섹션을 지정 가능 — 단, 그 셋 자체는 별도 라우트이므로 절대 이 state 값이
  // 되면 안 된다(렌더링할 케이스가 없어 빈 화면이 됨). EXTERNAL_SECTIONS 면 즉시 리다이렉트.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawInitialSection = searchParams.get('section') as DashboardSection | null;
  const initialSection: DashboardSection =
    rawInitialSection && !EXTERNAL_SECTIONS.includes(rawInitialSection)
      ? rawInitialSection
      : 'overview';
  const [section, setSection] = useState<DashboardSection>(initialSection);

  // 쿼리 → state 동기화. 외부에서 ?section= 으로 들어오면 그 섹션으로 전환하고 쿼리는 비운다.
  // history/materials/documents 는 이 페이지에서 렌더링하지 않으므로 곧장 해당 라우트로 보낸다.
  useEffect(() => {
    const q = searchParams.get('section') as DashboardSection | null;
    if (!q) return;
    const externalRoute = externalRouteFor(q, projectIdNum);
    if (externalRoute) {
      navigate(externalRoute, { replace: true });
      return;
    }
    if (q !== section) {
      setSection(q);
    }
    // URL 정리 — 새로고침 시 초기 섹션은 유지되지만 쿼리는 클린업.
    const next = new URLSearchParams(searchParams);
    next.delete('section');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToRoute = (route: string) => navigate(route);

  // 사이드바(md+)와 모바일 탭 내비(xs~sm)가 공유하는 섹션 변경 핸들러.
  // 히스토리/자재/문서 보관함은 별도 페이지로 navigate, 나머지는 페이지 내부 state 전환.
  const handleSectionChange = (s: DashboardSection) => {
    const externalRoute = externalRouteFor(s, projectIdNum);
    if (externalRoute) {
      navigate(externalRoute);
      return;
    }
    setSection(s);
  };

  const renderSection = () => {
    if (!Number.isFinite(projectIdNum)) {
      return (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            유효한 프로젝트 ID 가 없어 콘텐츠를 표시할 수 없습니다.
          </Typography>
        </Paper>
      );
    }

    switch (section) {
      case 'agent':
        return <ParkingAgentSection key={projectIdNum} projectId={projectIdNum} />;
      case 'overview':
        return (
          <OverviewSection
            projectId={projectId}
            projectIdNum={projectIdNum}
            onJumpToSection={setSection}
          />
        );
      case 'deploy':
        return (
          <DeploySection
            projectId={projectIdNum}
            projectName={projectData?.name || `프로젝트 ${projectId}`}
          />
        );
      // sidebar-merge-schedules: 일정/출장 일지/장애 이력 → 단일 통합 섹션.
      // 'journal' / 'incidents' 는 widget 의 onJumpToSection 콜백에서 들어왔을 때
      // 해당 카테고리 탭으로 진입하도록 라우팅한다.
      case 'history':
        // 히스토리는 별도 페이지에서 — 사이드바 클릭 시 navigate (아래 useEffect 처리).
        return null;
      case 'schedules':
        return (
          <UnifiedScheduleSection
            projectId={projectIdNum}
            siteName={siteName}
            defaultCategory="schedules"
          />
        );
      case 'journal':
        return (
          <UnifiedScheduleSection
            projectId={projectIdNum}
            siteName={siteName}
            defaultCategory="journal"
          />
        );
      case 'incidents':
        return (
          <UnifiedScheduleSection
            projectId={projectIdNum}
            siteName={siteName}
            defaultCategory="incidents"
          />
        );
      case 'photos':
        return <PhotoGalleryView projectId={projectIdNum} />;
      case 'network':
        return <NetworkSpreadsheet projectId={projectIdNum} />;
      case 'nvr':
        return <NvrTemplateSpreadsheet projectId={projectIdNum} />;
      case 'accounts':
        return <AccountSpreadsheet projectId={projectIdNum} />;
      case 'wallpad':
        return <WallpadMapSection siteName={siteName} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* 모바일에서는 좌측(브레드크럼)·우측(버튼) 그룹이 줄바꿈되도록 flexWrap.
          nowrap 없이 flex 가 줄어들면 글자가 세로로 깨진다. */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, md: 4 },
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            rowGap: 0.5,
            minWidth: 0,
          }}
        >
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectData?.name || `프로젝트 ${projectId}`}
            </Typography>
          </Breadcrumbs>
          {projectData?.location && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, whiteSpace: 'nowrap' }}
            >
              {projectData.location}
            </Typography>
          )}
          {projectData?.status && (
            <Chip
              size="small"
              label={
                projectData.status === 'active'
                  ? '● 운영중'
                  : projectData.status === 'maintenance'
                    ? '◐ 점검중'
                    : '○ 비활성'
              }
              color={projectData.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          {(constructionDate || finalDate) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
              {constructionDate && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  title="준공일"
                  data-testid="header-construction-date"
                >
                  <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    준공
                  </Typography>
                  <Typography variant="caption" color="text.primary" fontWeight={600}>
                    {constructionDate}
                  </Typography>
                </Box>
              )}
              {finalDate && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  title="완공일"
                  data-testid="header-final-date"
                >
                  <EventAvailableIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    완공
                  </Typography>
                  <Typography variant="caption" color="text.primary" fontWeight={600}>
                    {finalDate}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <DarkModeIcon fontSize="small" />
            )}
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(`/project/${projectId}/settings`)}
            sx={{ borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            현장 설정
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DashboardIcon />}
            onClick={() => {
              if (projectData?.dashboard_url) {
                window.open(projectData.dashboard_url, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={!projectData?.dashboard_url}
            title={projectData?.dashboard_url ? '대시보드 열기' : '대시보드 URL 미등록'}
            sx={{ borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            data-testid="header-dashboard-button"
          >
            대시보드
          </Button>
          <UserProfileMenu />
        </Box>
      </Paper>

      {/* md 미만: 사이드바가 숨겨지므로 가로 스크롤 탭으로 섹션 전환. */}
      <MobileSectionNav active={section} onChange={handleSectionChange} />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <DashboardSidebar active={section} onChange={handleSectionChange} />

        <Box component="main" sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
            {renderSection()}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectDashboardPage;
